import React, { useState, useEffect, useMemo, forwardRef, useImperativeHandle } from 'react';

const ReviewModeTab = forwardRef(function ReviewModeTab({ config, onUpdate, hideWizardSaveBar = false }, ref) {
  const [reviewMode, setReviewMode] = useState('MANUAL');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  const savedReviewMode = config?.reviewMode ?? 'MANUAL';
  const isDirty = useMemo(() => reviewMode !== savedReviewMode, [reviewMode, savedReviewMode]);

  useEffect(() => {
    if (config?.reviewMode) {
      setReviewMode(config.reviewMode);
    }
  }, [config]);

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    try {
      await onUpdate({ reviewMode });
      setMessage({ type: 'success', text: 'Review mode saved successfully!' });
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to save changes. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  useImperativeHandle(ref, () => ({
    saveStep: async () => {
      setSaving(true);
      setMessage(null);
      try {
        await onUpdate({ reviewMode });
        return { ok: true };
      } catch {
        return { ok: false, error: 'Failed to save review mode.' };
      } finally {
        setSaving(false);
      }
    }
  }), [reviewMode, onUpdate]);

  return (
    <div className="tab-panel">
      <div className="tab-header">
        <h2>Review Mode</h2>
        <p>Choose how claims are reviewed and approved after submission.</p>
      </div>

      <div className="rules-form">
        <div className="form-section">
          <div className="form-section-title">Select Review Mode</div>
          
          <div className="mode-selector">
            <div className="mode-option">
              <input
                type="radio"
                id="mode-auto"
                name="reviewMode"
                value="AUTO"
                checked={reviewMode === 'AUTO'}
                onChange={(e) => setReviewMode(e.target.value)}
                disabled={false}
              />
              <label htmlFor="mode-auto">
                <span className="mode-icon">⚡</span>
                <span className="mode-title">Automatic</span>
                <span className="mode-desc">Claims are automatically approved or rejected based on configured rules</span>
              </label>
            </div>
            
            <div className="mode-option">
              <input
                type="radio"
                id="mode-manual"
                name="reviewMode"
                value="MANUAL"
                checked={reviewMode === 'MANUAL'}
                onChange={(e) => setReviewMode(e.target.value)}
              />
              <label htmlFor="mode-manual">
                <span className="mode-icon">👁️</span>
                <span className="mode-title">Manual</span>
                <span className="mode-desc">Claims are queued for manual review by operators</span>
              </label>
            </div>
          </div>
        </div>

        <div className="form-section">
          <div className="form-section-title">Mode Comparison</div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #e2e8f0' }}>
                <th style={{ textAlign: 'left', padding: '0.75rem', color: '#475569' }}>Feature</th>
                <th style={{ textAlign: 'center', padding: '0.75rem', color: '#475569' }}>Automatic</th>
                <th style={{ textAlign: 'center', padding: '0.75rem', color: '#475569' }}>Manual</th>
              </tr>
            </thead>
            <tbody>
              <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                <td style={{ padding: '0.75rem' }}>Processing Speed</td>
                <td style={{ textAlign: 'center', padding: '0.75rem' }}>⚡ Instant</td>
                <td style={{ textAlign: 'center', padding: '0.75rem' }}>🕐 Depends on review</td>
              </tr>
              <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                <td style={{ padding: '0.75rem' }}>Human Verification</td>
                <td style={{ textAlign: 'center', padding: '0.75rem' }}>❌ None</td>
                <td style={{ textAlign: 'center', padding: '0.75rem' }}>✅ Required</td>
              </tr>
              <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                <td style={{ padding: '0.75rem' }}>Fraud Prevention</td>
                <td style={{ textAlign: 'center', padding: '0.75rem' }}>📋 Rule-based</td>
                <td style={{ textAlign: 'center', padding: '0.75rem' }}>🔍 Manual inspection</td>
              </tr>
              <tr>
                <td style={{ padding: '0.75rem' }}>Operator Workload</td>
                <td style={{ textAlign: 'center', padding: '0.75rem' }}>✅ None</td>
                <td style={{ textAlign: 'center', padding: '0.75rem' }}>📈 High</td>
              </tr>
            </tbody>
          </table>
        </div>

        {message && (
          <div className={`status-message ${message.type}`}>
            {message.type === 'success' ? '✓' : '✕'} {message.text}
          </div>
        )}

        {!hideWizardSaveBar && (
          <div className="save-bar">
            <button 
              className="btn-save" 
              onClick={handleSave}
              disabled={saving || !isDirty}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
});

export default ReviewModeTab;
